import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type ClassItem = { name: string; description?: string };
type Category = { title: string; items: ClassItem[] };

const CATEGORIES: Category[] = [
  {
    title: "Yoga & Relaxation",
    items: [
      {
        name: "Morning Yoga",
        description: "Start your day off right with a warm yoga class that wakes up your body and soul. You’ll move through simple, energizing sequences to awaken your body so you can tackle the day ahead with energy and vigour. The class mixes balance, standing and strengthening poses with energizing movements.",
      },
      { name: "Energy Flow" },
      { name: "Power Yoga" },
      { name: "26 Yoga + Breath" },
      { name: "Stretch & Relax" },
      { name: "Hot Hatha" },
    ],
  },
  {
    title: "Yoga-Fitness Fusion",
    items: [{ name: "Tone and Sculpt" }, { name: "Fit & Flow Daily" }, { name: "Pilates Core Fusion" }],
  },
  {
    title: "Fitness Focus",
    items: [{ name: "Hit it! Core & Booty Burn" }, { name: "Sculpt with Weights" }, { name: "Cardio Strength & Stretch" }],
  },
];

export default function ClassesSection() {
  const [activeName, setActiveName] = useState<string | null>(null);

  const active = useMemo(() => {
    for (const cat of CATEGORIES) {
      const found = cat.items.find((i) => i.name === activeName);
      if (found) return found;
    }
    return { name: activeName } as ClassItem;
  }, [activeName]);

  return (
      <section className="w-full bg-white">
        <div className="mx-auto w-full max-w-[1514px] px-4 sm:px-6 xl:px-0">
          {/* Title */}
          <div className="pt-4 sm:pt-10">
            <h2
                className="
              mx-auto w-fit
              text-center
              font-['Helvetica']
              font-medium
              text-[40px]
              sm:text-[48px]
              xl:text-[62px]
              text-[#2E5984]
            "
            >
              Classes
            </h2>
          </div>


          <div className="relative">
            <div className="mx-auto w-full grid grid-cols-1 gap-3 pb-20 md:grid-cols-3 xl:gap-[14px]">
              {CATEGORIES.map((cat) => (
                  <div
                      key={cat.title}
                      className="
                  rounded-[24px] bg-white
                  p-6
                  sm:p-8
                  xl:rounded-[10px] xl:px-[17px] xl:py-[17px]
                "
                  >
                    {/* Card title */}
                    <div className="flex items-center justify-center xl:px-[16px]">
                      <h3
                          className="
                      w-full
                      font-['Helvetica']
                      font-bold
                      text-[24px] leading-[30px]
                      sm:text-[28px] sm:leading-[34px]
                      xl:text-[36px] xl:leading-[41px]
                      xl:tracking-[-0.025em]
                      text-[#2E5984]
                    "
                      >
                        {cat.title}
                      </h3>
                    </div>

                    {/* List */}
                    <div className="xl:mt-[34px] flex flex-col items-start gap-3">
                      {cat.items.map((item) => {
                        const isActive = item.name === activeName;

                        return (
                            <button
                                key={item.name}
                                type="button"
                                onClick={() => setActiveName(item.name)}
                                className={[
                                  "inline-flex items-center text-left min-h-[40px] px-3 py-2 xl:h-[36px] xl:py-[8px] xl:px-[16px] border border-[#2E5984] text-[#2E5984] rounded-full xl:rounded-[100px]",
                                  isActive
                                      ? [
                                        "border border-[#2E5984] text-[#2E5984]",
                                        "rounded-full",
                                        // на xl фиксируем как в макете
                                        "xl:rounded-[100px]",
                                      ].join(" ")
                                      : "border border-transparent text-[rgba(0,0,0,0.8)] hover:text-[#2E5984]",
                                ].join(" ")}
                            >
                        <span
                            className={[
                              "font-['Helvetica']",
                              isActive ? "font-bold text-[#2E5984]" : "font-normal text-[rgba(0,0,0,0.8)]",
                              "text-[18px] sm:text-[20px]",
                              "xl:text-[26px]",
                            ].join(" ")}
                        >
                          {item.name}
                        </span>
                            </button>
                        );
                      })}
                    </div>
                  </div>
              ))}

              <AnimatePresence mode="wait">
                {activeName && (
                    <motion.div
                        key={activeName}
                        className="description-cat-box mt-6 sm:mt-8 xl:mt-[34px] absolute bottom-0 right-20"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                    >
                      <div
                          className="
          w-full
          rounded-[14px]
          bg-white
          shadow-[0px_12px_26px_rgba(0,0,0,0.12)]
          px-5 py-5
          sm:px-7 sm:py-6
          xl:w-[973px] xl:h-[224px]
          xl:rounded-[16px]
          xl:px-0 xl:py-0
          xl:shadow-none
          xl:[filter:drop-shadow(3px_-1px_30.4px_rgba(0,0,0,0.25))]
        "
                      >
                        <div
                            className="
            flex flex-col gap-3
            xl:flex-row xl:gap-0
            xl:h-full
          "
                        >
                          {/* Left title */}
                          <div className="xl:w-[320px] xl:pl-[52px] xl:h-full">
                            <div
                                className="
      flex items-center h-full
      font-['Helvetica']
      font-bold
      text-[#2E5984]
      text-[20px]
      sm:text-[22px]
      xl:text-[28px]
    "
                            >
                              {active.name}
                            </div>
                          </div>

                          {/* Right text */}
                          <div className="xl:flex-1 xl:pr-[36px] xl:pt-[34px]">
                            <p
                                className="
                font-['Inter']
                font-medium
                text-[14px] leading-[22px]
                sm:text-[16px] sm:leading-[24px]
                xl:text-[20px] xl:leading-[28px]
                text-[rgba(0,0,0,0.8)]
              "
                            >
                              {active.description ?? active.name}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>


          {/* Pixel-perfect сдвиг (вынес отдельно, чтобы не влиять на mobile) */}
          <div className="hidden xl:block h-[60px]"/>
        </div>

        {/* Применяем сдвиг только на xl через отдельный селектор */}
        <style>{`
        @media (min-width: 1280px) {
          /* Сдвигаем карточку описания вправо как left:447 в артборде */
          section .shadow-\\[0px_12px_26px_rgba\\(0\\,0\\,0\\,0\\.12\\)\\] {
            /* не трогаем — просто пример, не используем */
          }
        }
      `}</style>
      </section>
  );
}
